import { zodResolver } from "@hookform/resolvers/zod";
import { observer, useLocalObservable } from "mobx-react-lite";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { OptionType, useGetOptionsQuery } from "@/api/admin";
import {
  BRAND_LOGO_ACCEPTED_TYPES,
  BrandLogoMediaValidation,
  useGetBrandAvailableLegalEntityByGroupId,
} from "@/api/admin/brands";
import { commonApi } from "@/api/common";
import {
  CustomCardBody,
  CustomLabel,
  ErrorForm,
  LabelWithOptional,
  Loader,
  SimpleText,
  Subtitle,
} from "@/components/atoms";
import {
  InputControl,
  SelectControl,
  TextareaControl,
} from "@/components/molecules";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useWizardStepDraft } from "@/modules/wizardDraft";
import { rootStore } from "@/store/RootStore";
import type { FormProps, FormRefType } from "@/types/common";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";
import { mapToTreeData } from "@/utils/mapToTreeData";

import { MediaStore, MediaUploader, UploadedFiles } from "../MediaUploader";
import { type BrandFormDetailsType, FormSchema } from "./types";

export type GroupSelectionMode = {
  canChangeGroup: boolean;
  showGroupSelection: boolean;
  isGroupSelectDisabled?: boolean;
};

type BrandFormDetailsProps = FormProps<BrandFormDetailsType> &
  GroupSelectionMode & {
    draftEntityId?: string;
  };

const BrandFormDetailsInner = forwardRef<
  FormRefType<BrandFormDetailsType>,
  BrandFormDetailsProps
>(
  (
    {
      defaultValues,
      onSubmit,
      onValidityChange,
      id,
      setValidData,
      children,
      classes,
      canChangeGroup = false,
      showGroupSelection = false,
      isGroupSelectDisabled,
      draftEntityId,
    },
    ref
  ) => {
    const groupId = defaultValues.groupId;
    const isGroupDisabled =
      isGroupSelectDisabled ?? (!canChangeGroup || !!groupId);
    const isBrand = rootStore.authStore.isBrandAccess;

    const {
      data: optionsData,
      isPending: isOptionPending,
      isSuccess,
    } = useGetOptionsQuery([
      OptionType.Currencies,
      OptionType.Countries,
      OptionType.Groups,
    ]);

    const form = useForm<BrandFormDetailsType>({
      resolver: zodResolver(FormSchema),
      defaultValues,
      mode: "onChange",
      reValidateMode: "onChange",
    });

    useWizardStepDraft({
      form,
      wizard: "brand",
      entityId: draftEntityId,
      step: "details",
    });

    const {
      control,
      handleSubmit,
      watch,
      formState: { isValid },
      reset,
    } = form;

    const [errorLogo, setErrorLogo] = useState<string | null>(null);

    const mediaStore = useLocalObservable(
      () => new MediaStore(undefined, BrandLogoMediaValidation)
    );

    // Watch groupId from form to reload legal entities when it changes
    const watchedGroupId = watch("groupId");
    const currentGroupId = watchedGroupId || groupId;

    const { data: legalEntitiesData, isPending: isLegalEntitiesPending } =
      useGetBrandAvailableLegalEntityByGroupId(currentGroupId || "");

    const treeData = mapToTreeData(legalEntitiesData ?? []);

    useExposeFormValues(ref, form);

    useEffect(() => {
      reset(defaultValues, { keepErrors: true });
    }, [
      defaultValues?.id,
      defaultValues?.country,
      defaultValues?.currency,
      isSuccess,
      optionsData,
      reset,
    ]);

    useEffect(() => {
      const subscription = watch(values => {
        setValidData?.(values as BrandFormDetailsType);
      });

      return () => subscription.unsubscribe();
    }, [watch, setValidData]);

    const prevGroupId = useRef<string | undefined>(watchedGroupId);

    useEffect(() => {
      const hasChanged = prevGroupId.current !== watchedGroupId;
      const prevValue = prevGroupId.current;
      prevGroupId.current = watchedGroupId;

      if (!hasChanged) return;

      if (prevValue) {
        form.setValue("legalEntityId", undefined);
      }

      if (canChangeGroup && watchedGroupId && optionsData?.groups) {
        const selectedGroup = optionsData.groups.find(
          group => group.id === watchedGroupId
        );

        if (selectedGroup) {
          form.setValue("country", selectedGroup.country);
          form.setValue("currency", selectedGroup.currency);
        }
      }
    }, [watchedGroupId, canChangeGroup, optionsData?.groups, form]);

    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    useEffect(() => {
      if (defaultValues?.logoUrl) {
        mediaStore.restoreFromPayload([
          {
            id: defaultValues.logoUrl,
            previewUrl: defaultValues.logoUrl,
            type: "image/jpeg",
          },
        ]);
      }
    }, [defaultValues, mediaStore]);

    const handleSubmitWithMedia = async (formValues: BrandFormDetailsType) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { mediaItemId, ...rest } = formValues;

        let finalMediaItemId: string | undefined | null = undefined;

        if (mediaStore.mediaFiles.length > 0) {
          const mediaFile = mediaStore.mediaFiles[0];

          if (mediaFile.file) {
            const response = await commonApi.mediaUpload(mediaFile.file);
            finalMediaItemId = response.id;
          }
        } else {
          finalMediaItemId = null;
        }

        const payload = {
          ...rest,
          mediaItemId: finalMediaItemId,
        };

        onSubmit(payload);
        mediaStore.resetStore();
      } catch (error) {
        setErrorLogo("Error in uploading");
      }
    };

    if (isOptionPending || !optionsData || !isSuccess) {
      return <Loader fullscreen />;
    }

    return (
      <Form {...form}>
        <form
          onSubmit={handleSubmit(handleSubmitWithMedia)}
          id={id}
          data-testid="brand-form-details"
        >
          <CustomCardBody classes={cn(classes)}>
            <div className="mb-0 grid grid-cols-1 align-center gap-x-5 text-gray-950">
              <div className="parent">
                <div className="responsive-fields">
                  {showGroupSelection && (
                    <div className="flex flex-col gap-1 mb-5.5">
                      <CustomLabel htmlFor="group" label="Group" />
                      <SelectControl<BrandFormDetailsType>
                        form={form}
                        nameSelect="groupId"
                        placeholder="Select"
                        itemsList={optionsData?.groups || []}
                        triggerProps={{
                          "data-testid": "brand-form-details-group",
                        }}
                        disabled={isGroupDisabled}
                      />
                    </div>
                  )}

                  <InputControl<BrandFormDetailsType>
                    formControl={control}
                    label="Brand name (English)"
                    nameField="nameEn"
                    data-testid="brand-form-details-name-english"
                  />
                  <InputControl<BrandFormDetailsType>
                    formControl={control}
                    label="Brand name (Arabic)"
                    nameField="nameAr"
                    disabled={isBrand}
                    data-testid="brand-form-details-name-arabic"
                    dir="rtl"
                  />
                </div>
              </div>
            </div>

            <div className="parent">
              <div className="responsive-fields">
                <div className="flex flex-col gap-1 text-sm mb-5.5">
                  <LabelWithOptional label="Legal entity" />
                  <SelectControl<BrandFormDetailsType>
                    form={form}
                    nameSelect="legalEntityId"
                    placeholder="Select"
                    isTree
                    itemsList={treeData}
                    disabled={!currentGroupId || isLegalEntitiesPending}
                    triggerProps={{
                      "data-testid": "brand-form-details-legal-entity",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-1 sm:gap-5 w-full flex-col sm:flex-row">
              <div className="flex flex-col gap-1 flex-1">
                <LabelWithOptional
                  label="Description (English)"
                  classes="text-sm"
                />
                <TextareaControl<BrandFormDetailsType>
                  formControl={control}
                  nameField="descriptionEn"
                  className={cn(isBrand && "w-[74%]")}
                  data-testid="brand-form-details-description"
                />
              </div>

              <div className="flex flex-col gap-1 flex-1">
                <LabelWithOptional
                  label="Description (Arabic)"
                  classes="text-sm"
                />
                <TextareaControl<BrandFormDetailsType>
                  formControl={control}
                  nameField="descriptionAr"
                  className={cn(isBrand && "w-[74%]")}
                  data-testid="brand-form-details-description"
                  dir="rtl"
                />
              </div>
            </div>

            {optionsData?.countries && optionsData?.currencies && (
              <div className="flex align-center gap-x-0 sm:gap-x-5 gap-y-5 sm:gap-y-0 text-gray-950 mb-6 flex-col sm:flex-row">
                <SelectControl<BrandFormDetailsType>
                  form={form}
                  nameSelect="country"
                  label="Country"
                  placeholder="Select"
                  itemsList={optionsData?.countries || []}
                  triggerProps={{ "data-testid": "brand-form-details-country" }}
                  disabled={!canChangeGroup || !!groupId}
                />

                <SelectControl<BrandFormDetailsType>
                  form={form}
                  nameSelect="currency"
                  label="Currency"
                  placeholder="Select"
                  itemsList={optionsData?.currencies || []}
                  disabled={!canChangeGroup || !!groupId}
                  triggerProps={{
                    "data-testid": "brand-form-details-currency",
                  }}
                />
              </div>
            )}
            <div className="mb-4">
              <Subtitle classes="mb-1" title="Logo" />
              <SimpleText classes="mb-6">
                Must be a .jpg, .gif, .svg or .png file smaller than 3 MB and at
                least 500px by 500px
              </SimpleText>
              <div className="flex gap-2">
                {mediaStore.images.map((media, index) => (
                  <UploadedFiles
                    media={media}
                    key={index}
                    index={index}
                    mediaStore={mediaStore}
                  />
                ))}
                <MediaUploader
                  acceptedTypes={BRAND_LOGO_ACCEPTED_TYPES}
                  disabled={mediaStore.isImageLimitReached}
                  mediaStore={mediaStore}
                  onError={setErrorLogo}
                />
              </div>
              {errorLogo && <ErrorForm message={errorLogo} />}
            </div>
            {children}
          </CustomCardBody>
        </form>
      </Form>
    );
  }
);

BrandFormDetailsInner.displayName = "BrandFormDetails";

const BrandFormDetails = observer(BrandFormDetailsInner);
export default BrandFormDetails;
